import { useMutation, useQuery } from "@tanstack/react-query";

import axiosInstance from "./axios";
import {
  CreateVendorRequest,
  GetVendorDetailResponse,
  GetVendorPurchasesRequest,
  GetVendorPurchasesResponse,
  GetVendorsRequest,
  GetVendorsResponse,
  GetVendorPaymentHistoryRequest,
  GetVendorPaymentHistoryResponse,
} from "../types/vendorTypes";

export const useGetAllVendors = (params: GetVendorsRequest) => {
  return useQuery({
    queryKey: ["useGetAllVendors", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/vendors", {
        params,
      });

      return data as GetVendorsResponse;
    },
    enabled: !!params,
  });
};

export const useGetVendorById = (vendorId?: string | null) => {
  return useQuery({
    queryKey: ["useGetVendorById", vendorId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/vendors/${vendorId}`);
      return data as GetVendorDetailResponse;
    },
    enabled: Boolean(vendorId && /^\d+$/.test(vendorId)),
  });
};

export const useGetVendorPurchases = (params: GetVendorPurchasesRequest) => {
  const { vendorId, ...queryParams } = params;

  return useQuery({
    queryKey: ["useGetVendorPurchases", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/vendors/${vendorId}/purchases`, {
        params: queryParams,
      });

      return data as GetVendorPurchasesResponse;
    },
    enabled: Boolean(vendorId && /^\d+$/.test(vendorId)),
  });
};

export const useCreateVendor = () => {
  return useMutation({
    mutationKey: ["useCreateVendor"],
    mutationFn: async (postData: CreateVendorRequest) => {
      const { data } = await axiosInstance.post("/vendors", postData);
      return data;
    },
  });
};

export const useUpdateVendor = () => {
  return useMutation({
    mutationKey: ["useUpdateVendor"],
    mutationFn: async ({ id, data: postData }: { id: number; data: CreateVendorRequest }) => {
      const { data } = await axiosInstance.put(`/vendors/${id}`, postData);
      return data;
    },
  });
};

export const useDeleteVendor = () => {
  return useMutation({
    mutationKey: ["useDeleteVendor"],
    mutationFn: async (id: number) => {
      const { data } = await axiosInstance.delete(`/vendors/${id}`);
      return data;
    },
  });
};

export type VendorSettlementRequest = {
  purchaseEntryId: number;
  amountPaid: number;
  paymentDate: string;
  paymentMode: "CASH" | "CARD" | "ONLINE_PAYMENT" | "CHEQUE" | "CREDIT" | "MIXED";
  chqNumber?: string;
  cardNumber?: string;
  onlinePaymentRefNo?: string;
};

export const useVendorSettlement = () => {
  return useMutation({
    mutationKey: ["useVendorSettlement"],
    mutationFn: async (postData: VendorSettlementRequest) => {
      const { data } = await axiosInstance.post(
        "/purchase-entry/settlement",
        postData,
      );
      return data;
    },
  });
};

export const useGetVendorPaymentHistory = (params: GetVendorPaymentHistoryRequest) => {
  const { vendorId, ...queryParams } = params;

  return useQuery({
    queryKey: ["useGetVendorPaymentHistory", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/vendors/${vendorId}/payments`, {
        params: queryParams,
      });

      return data as GetVendorPaymentHistoryResponse;
    },
    enabled: Boolean(vendorId && /^\d+$/.test(vendorId)),
  });
};

export const useDownloadVendorPaymentHistory = () => {
  return useMutation({
    mutationKey: ["useDownloadVendorPaymentHistory"],
    mutationFn: async (vendorId: number | string) => {
      const { data } = await axiosInstance.get(
        `/vendors/${vendorId}/payments/export/excel`,
        {
          responseType: "blob",
        }
      );
      return data;
    },
    onSuccess: (blob, vendorId) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `vendor_${vendorId}_payment_history.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadVendorPurchasesExcel = () => {
  return useMutation({
    mutationKey: ["useDownloadVendorPurchasesExcel"],
    mutationFn: async ({
      vendorId,
      ...queryParams
    }: {
      vendorId: number | string;
      search?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const { data } = await axiosInstance.get(
        `/vendors/${vendorId}/purchases/export/excel`,
        {
          params: queryParams,
          responseType: "blob",
        }
      );
      return data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `vendor_${variables.vendorId}_purchase_history.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};
